import React, { FC, useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  TooltipItem
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import SwitchSelector from 'react-switch-selector';

import HeaderBtn from '../../headerBtn/headerBtn';

import { ReactComponent as SettingIcon } from '../../../assets/icons/settings.svg';
import { ReactComponent as IncreaseIcon } from '../../../assets/icons/increase.svg';
import { ReactComponent as ReduceIcon } from '../../../assets/icons/reduce.svg';
import { ReactComponent as ArrowIcon } from '../../../assets/icons/chevron.svg';
import { ReactComponent as FiltersIcon } from '../../../assets/icons/filter.svg';
import { ReactComponent as ActiveFiltersIcon } from '../../../assets/icons/filter_active.svg';

import { AppDispatch } from "../../../redux/reducer/store";
import { actionAnalyticOos } from '../../../redux/action/charts/actionCharts';
import { reducerChangeDataCharts, reducerEditTypeChart, reducerChartsFilters } from '../../../redux/reducer/charts/reducer/reducerCharts';

import ChartComponent from '../../custom/chartComponet/chartComponent';
import ChartLoader from '../../custom/chartLoader/chartLoader';
import ScrollingLegend from '../../custom/legend/legend';

import {
  createFormatDateFunction,
  filteredZeroData,
  reverseChartsList,
  lineChartList,
  monthOptions,
  defaultFilter,
  normList,
  tickData,
  getColorOptions
} from '../../../utils/chartsData';
import { formatDateForMapReport } from '../../../utils/formatDate';
import { IChartOptions, IFiltersCharts, IContextOptions } from '../../../types/chartsInterface';

import "../charts.scss";

ChartJS.register(
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  ChartDataLabels,
  PointElement,
  LineElement
);

const switchOptoins = [
  { value: 0, label: 'Разъединить' },
  { value: 1, label: 'Объединить' },
];

const typesCharts = [
  { value: 'bar', label: 'Гистограмма' },
  { value: 'barStackedNorm', label: 'Нормированная гистограмма с накоплением' },
  { value: 'lineBarNorm', label: 'Нормированная линейчатая с накоплением' },
  { value: 'lineChartStackedNorm', label: 'Нормированный график с накоплением' },
  { value: 'pie', label: 'Круговая диаграмма' },
  { value: 'doughnut', label: 'Кольцевая диаграмма' },
];

const OssChart: FC<IChartOptions> = ({ id, copyChart, isCopy, pageId }) => {
  const dispatch: any = useDispatch<AppDispatch>();
  const listRef = useRef<HTMLDivElement | null>(null);
  const legendRef = useRef<HTMLDivElement>(null);
  const scrollStep = 21.5;

  const {
    pending,
    desktopData,
    settingCharts,
    desktopInputValues,
  } = useSelector((state: any) => state.charts?.chartsList);

  const {
    filters,
    areasOptions,
    chainsOptions,
    regionsOptions,
    projectOptions,
    contractsOptions,
  } = useSelector((state: any) => state.userReports.userReports);
  const { darkTheme } = useSelector((state: any) => state.theme.theme);

  const ticks = {
    ...tickData,
    color: darkTheme.isDarkTheme ? 'white' : 'black'
  };

  const index = desktopData[pageId || 0]?.find((el: { id: number }) => el.id === id) || {};

  const {
    dataChartsHotlinesPercentage,
    dataOssChartsPrecentege,
    dataChartsHotlines,
    dataOssCharts,
    dataCircle,
    periodList,
    ossCharts,
    labels,
    type
  } = index;

  const colorList = getColorOptions(settingCharts?.oss);
  const ossFilters = desktopInputValues?.[Number(pageId) || 0]?.[id] || null;

  const [isListRightFiltersPosition, setIsListRightFiltersPosition] = useState<boolean>(false);
  const [isOpenSectionCharts, setIsOpenSectionCharts] = useState<boolean>(false);
  const [isListRightPosition, setIsListRightPosition] = useState<boolean>(false);
  const [isChangeData, setIsChangeData] = useState<boolean>();
  const [isIncreasedSize, setIsIncreasedSize] = useState<boolean>(false);
  const [isHiddenLabel, setIsHiddenLabel] = useState<string[]>([]);
  const [isOpenSetting, setIsOpenSetting] = useState<boolean>(false);
  const [isVisiblelabel, setIsVisiblelabel] = useState<any[]>([]);
  const [isloader, setIsloader] = useState<boolean>(true);
  const [isPeriod, setIsPeriod] = useState<string[]>([]);
  const [isOn, setIsOn] = useState<number>(0);
  const [isData, setIsData] = useState([]);

  const typeCircle = type === 'pie' || type === 'doughnut';
  const initialSelectedIndex = switchOptoins.findIndex(option => option.value === isOn);
  const isLabels = isOn ? ['в наличии', 'проблема с товаром'] : labels;

  const axisX = [
    {
      formatDate: (data: string) => {
        return formatDateForMapReport(data);
      }
    },
    {
      formatDate: createFormatDateFunction(contractsOptions)
    },
    {
      formatDate: createFormatDateFunction(projectOptions)
    },
    {
      formatDate: createFormatDateFunction(chainsOptions)
    },
    {
      formatDate: createFormatDateFunction(areasOptions)
    },
    {
      formatDate: createFormatDateFunction(regionsOptions)
    },
    {
      formatDate: createFormatDateFunction(monthOptions)
    },
  ];

  const isHasOffset = ['barStackedNorm', 'lineChartStackedNorm', 'lineBarNorm'];

  const chartsDatasetCircle = {
    data: isOn ? dataCircle[1] : dataCircle[0],
    labels: labels,
    borderWidth: 0
  };

  const data = {
    labels: periodList ? isPeriod : [],
    datasets: isData,
  };

  const optionsDefault = {
    responsive: true,
    indexAxis: type === 'lineBarNorm' ? 'y' as const : 'x' as const,
    layout: {
      padding: {
        top: -10,
      }
    },
    scales: {
      x: {
        stacked: normList.includes(type),
        max: type === 'lineBarNorm' ? 100 : undefined,
        beginAtZero: true,
        ticks: ticks,
        grid: {
          color: darkTheme.isDarkTheme ? 'white' : 'black'
        }
      },
      y: {
        stacked: normList.includes(type),
        max: type === 'barStackedNorm' ? 100 : undefined,
        beginAtZero: true,
        ticks: ticks,
        grid: {
          color: darkTheme.isDarkTheme ? 'white' : 'black'
        }
      }
    },
    plugins: {
      tooltip: {
        mode: type === 'bar' ? 'index' : 'nearest',
        intersect: type !== 'bar',
        callbacks: {
          label: function (tooltipItem: TooltipItem<typeof type>) {
            if (tooltipItem) {
              const label = tooltipItem.dataset.label
              const dataset = tooltipItem.formattedValue;
              if (Number(dataset) > 0 || type !== 'bar') {
                const postfix = normList.includes(type) ? '%' : '';
                return `${label}: ${dataset}${postfix}`;
              }
              return null;
            }
          }
        }
      },
      legend: {
        display: false,
      },
      datalabels: {
        anchor: 'center' as const,
        align: 'center' as const,
        formatter: (value: number, context: IContextOptions) => {
          if (value > 0 && !lineChartList.includes(type)) {
            const { chart } = context;
            const barWidth = chart.getDatasetMeta(context.datasetIndex).data[context.dataIndex].$animations.width._to;
            const barHeigth = chart.getDatasetMeta(context.datasetIndex).data[context.dataIndex].$animations.height._to;
            const widthSize = value.toFixed().length * 15;

            if (barHeigth > 13 && barWidth >= widthSize) {
              return value < 8 ? '' : `${value.toFixed()}${normList.includes(type) ? '%' : ''}`;
            } else {
              return '';
            }
          } else {
            return '';
          }
        },
        color: '#000',
        offset: isHasOffset.includes(type) ? -13 : -5,
        display: true,
      },
    },
  };

  const optionsForCircle = {
    responsive: true,
    indexAxis: 'x' as const,
    layout: {
      padding: {
        bottom: 10,
        top: -10,
      }
    },
    plugins: {
      tooltip: {
        callbacks: {
          label: function (tooltipItem: TooltipItem<typeof type>) {
            const dataset = tooltipItem.formattedValue;

            return `${isLabels[tooltipItem.dataIndex]}: ${dataset}%`;
          }
        }
      },
      legend: {
        display: false,
      },
      datalabels: {
        anchor: 'center' as const,
        align: 'start' as const,
        formatter: (value: number, context: IContextOptions) => {
          const dataset = context.chart.data.datasets[0].data;
          const total = dataset.reduce((sum: number, val: number) => sum + val, 0);
          const percentage = (value / total) * 100;
          return percentage < 3 ? '' : percentage.toFixed(2) + '%';
        },
        color: '#000',
        offset: isIncreasedSize || type === 'pie'
          ? -40 : -13,
        display: true,
      },
    },
  };

  const actualOptionsChart = typeCircle ? optionsForCircle : optionsDefault;

  const getChangeData = (data: IFiltersCharts) => {
    const changes: IFiltersCharts = {};

    for (const key in defaultFilter) {
      if (defaultFilter.hasOwnProperty(key)) {
        // @ts-ignore
        if (JSON.stringify(defaultFilter[key]) !== JSON.stringify(data[key])) {
          // @ts-ignore
          changes[key] = data[key];
        }
      }
    }

    for (const key in data) {
      if (data.hasOwnProperty(key) && !defaultFilter.hasOwnProperty(key)) {
        // @ts-ignore
        changes[key] = data[key];
      }
    }

    setIsChangeData(Object.keys(changes).length > 0);
    dispatch(reducerChangeDataCharts({ hasChanged: Object.keys(changes).length > 0, changes }));
  };

  const handleClickSetting = () => {
    if (isOpenSetting) {
      setIsOpenSectionCharts(false);
    } else {
      getChangeData(ossFilters);
    }
    setIsOpenSetting(!isOpenSetting);
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (listRef.current && !listRef.current.contains(event.target as Node)) {
      setIsOpenSetting(false);
      setIsOpenSectionCharts(false);
    }
  };

  const handleClickOnLegend = (name: string) => {
    if (!isHiddenLabel.includes(name)) {
      setIsHiddenLabel((prevItems: string[]) => {
        return [...prevItems, name];
      });
    } else {
      setIsHiddenLabel((prevItems: string[]) => {
        return prevItems.filter(item => item !== name);
      });
    }
  };

  const handleClickFilterOnType = (name: string, index: number) => {
    setIsOn(0);
    dispatch(reducerEditTypeChart({ id, pageId, data: name }));
    setIsHiddenLabel([]);
  };

  const handleChangeSwitchSelector = (newValue: any) => {
    setIsOn(newValue);
    dispatch(reducerChartsFilters({
      pageId,
      id, data:
      {
        ...ossFilters,
        status: newValue
      }
    }));
  };

  useEffect(() => {
    if (ossFilters) {
      getChangeData(ossFilters);
    }
    // eslint-disable-next-line
  }, [ossFilters]);

  useEffect(() => {
    if (isPeriod.length > 0) {
      const timeoutId = setTimeout(() => {
        const updatedData = typeCircle
          ? [chartsDatasetCircle]
          : dataOssCharts
            ? isOn ?
              normList.includes(type) ? dataOssChartsPrecentege : dataOssCharts
              : normList.includes(type) ? dataChartsHotlinesPercentage : dataChartsHotlines
            : [];
        const directionData = reverseChartsList.includes(type) ? updatedData.slice().reverse() : updatedData;
        setIsVisiblelabel(typeCircle ? isLabels : filteredZeroData(updatedData));
        setIsData(directionData);
      }, 500);

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line
  }, [isPeriod, type, isOn]);

  useEffect(() => {
    const period = ossFilters && ossCharts ? periodList.map((el: string) => {
      if (ossFilters?.group?.value === '7') {
        const parts = el.split('W');
        return parseInt(parts[1], 10);
      } else if (ossFilters?.group?.value === '8') {
        return axisX[6].formatDate(el);
      } else {
        if (ossFilters?.group?.value === '9') {
          return el;
        } else {
          return ossFilters?.group?.value > 5 ? axisX[0].formatDate(el) : axisX[ossFilters?.group?.value].formatDate(el);
        }
      }
    }) : [];
    setIsPeriod(period);
    // eslint-disable-next-line
  }, [periodList])

  useEffect(() => {
    if (isOpenSetting) {
      const listEl = listRef.current;
      if (listEl) {
        const list = listEl.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        setIsListRightPosition(list.right + 168 > viewportWidth);
        setIsListRightFiltersPosition(list.right + 505 > viewportWidth);
      }
    }
    // eslint-disable-next-line
  }, [isOpenSetting]);

  useEffect(() => {
    const handleWheel = (event: WheelEvent) => {
      event.preventDefault();

      if (legendRef.current) {
        const currentScrollTop = legendRef.current.scrollTop;
        const newScrollTop =
          event.deltaY > 0
            ? currentScrollTop + scrollStep
            : currentScrollTop - scrollStep;

        legendRef.current.scrollTo({
          top: newScrollTop,
          behavior: 'smooth',
        });
      }
    };

    const legendElement = legendRef.current;

    if (legendElement) {
      legendElement.addEventListener('wheel', handleWheel, { passive: false });
    }

    return () => {
      if (legendElement) {
        legendElement.removeEventListener('wheel', handleWheel);
      }
    };
  }, [scrollStep, isloader]);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (filters) {
      setIsloader(true);
      dispatch(actionAnalyticOos(id, pageId)).then(() => {
        setIsloader(false);
      });
    }
    setIsOn(ossFilters?.status || 0);
    // eslint-disable-next-line
  }, [filters]);

  useEffect(() => {
    if (pending[pageId || 0]) {
      setIsloader(pending[pageId || 0]?.includes(id) ? true : false);
    }
    // eslint-disable-next-line
  }, [pending]);

  return (
    <div className={`content-charts ${isIncreasedSize ? 'content-charts_active' : ''}`}>
      <div className='charts__header'>
        <p className='charts__title'>{`OOS ${isCopy ? '(дубль)' : ''}`}</p>
        <div className='switchContainer switchContainer_precent' style={{ width: '195px' }}>
          <SwitchSelector
            options={switchOptoins}
            onChange={handleChangeSwitchSelector}
            forcedSelectedIndex={initialSelectedIndex}
            fontColor={"#000"}
            name={`checkStatus_${id}`}
          />
        </div>
        <div className='charts__btn'>
          <div>
            {!isIncreasedSize && <IncreaseIcon className='charts__icon' onClick={() => setIsIncreasedSize(true)} />}
            {isIncreasedSize && <ReduceIcon className='charts__icon' onClick={() => setIsIncreasedSize(false)} />}
          </div>
          <div className='charts__headerbtn'>
            <HeaderBtn
              content=" "
              purpose="filtersCharts"
              modalTitle="Настройка отображения данных"
              selectId={id}
              action={actionAnalyticOos}
              inputValuesCharts={ossFilters}
            />
            {(!isChangeData || !ossFilters) &&
              <FiltersIcon
                className={`charts__icon charts__icon_filters`}
                onClick={handleClickSetting} />}
            {isChangeData && ossFilters &&
              <ActiveFiltersIcon
                className={`charts__icon charts__icon_filters`}
                onClick={handleClickSetting} />}
          </div>
          <div ref={listRef}>
            <SettingIcon className='charts__icon' onClick={handleClickSetting} />
            {isOpenSetting &&
              <ul className={`charts__setting ${isListRightPosition ? 'charts__setting_right' : ''}`}>
                <li onClick={() => {
                  handleClickSetting();
                  copyChart(id);
                }}>Дублировать</li>
                <li>
                  <HeaderBtn
                    content="Удалить"
                    purpose="removeCharts"
                    modalTitle="Удалить диаграмму?"
                    selectId={id} />
                </li>
                <li
                  style={{ position: 'relative' }}
                  onClick={() => {
                    setIsOpenSectionCharts(!isOpenSectionCharts);
                  }}
                >Тип диаграммы <ArrowIcon className='charts__icon' />
                  {isOpenSectionCharts &&
                    <ul className={`charts__setting charts__setting_list ${isListRightFiltersPosition ? 'charts__setting_list_typeRight' : ''}`}>
                      {typesCharts.map((el: { value: string, label: string }, index: number) => (
                        <li
                          key={index}
                          className={type === el.value ? 'item_active' : ''}
                          onClick={() => handleClickFilterOnType(el.value, index)}
                        >{el.label}</li>
                      ))}
                    </ul>}
                </li>
                <li>
                  <HeaderBtn
                    content="Скриншот"
                    purpose="screenCharts"
                    modalTitle="Сделать скриншот"
                    dataCharts={{
                      data,
                      name: `OOS ${isCopy ? '(дубль)' : ''}`,
                      type: type,
                      options: actualOptionsChart,
                      isIncreasedSize,
                      labels: isLabels,
                      isHiddenItem: isHiddenLabel,
                      colorOptions: colorList,
                      chartType: 'oss'
                    }} />
                </li>
                <li>
                  <HeaderBtn
                    content="Настройка цвета"
                    purpose="changeColor"
                    modalTitle="Настройка цвета"
                    dataCharts={{ type: "oss", labels, isVisiblelabel }}
                  />
                </li>
                {/* <li onClick={handleClickSetting}>Выгрузить данные</li> */}
              </ul>}
          </div>
        </div>
      </div>

      {isloader ?
        <ChartLoader />
        : ossCharts?.length === 0
          ? <p className='charts__text'>Данные по наличию товаров в выбранных вами фильтрах не найдены. Убедитесь, что вы заказали услугу, расширьте рассматриваемый период  или географический охват.
          </p>
          : <>
            <ChartComponent
              type={type}
              data={data}
              isHiddenItem={isHiddenLabel}
              options={actualOptionsChart}
              isIncreasedSize={isIncreasedSize}
              loader={isloader}
              chartType={'oss'}
              colorOptions={colorList}
            />
            <ScrollingLegend
              colorOptions={colorList}
              handleClickOnLegend={handleClickOnLegend}
              isIncreasedSize={isIncreasedSize}
              isVisiblelabel={isVisiblelabel}
              isHiddenLabel={isHiddenLabel}
              isLabels={isLabels}
              type={type}
            />
          </>
      }
    </div>
  )
};

export default OssChart;